import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import * as api from '../../apis';

const CalendarFeedModal = ({ unitType, unitId, approvalStatuses, onClose }) => {
	const [feedUrl, setFeedUrl] = useState(null);
	const [loading, setLoading] = useState(true);
	const [reissuing, setReissuing] = useState(false);
	const [error, setError] = useState(null);
	const buildPayload = useCallback(() => ({
		unit_type: unitType,
		unit_id: unitId ? Number(unitId) : undefined,
		approval_statuses: approvalStatuses?.length > 0 ? approvalStatuses : undefined,
	}), [unitType, unitId, approvalStatuses]);

	const loadOrCreateFeed = useCallback(() => {
		setError(null);
		return api.createCalendarFeed(buildPayload()).then((res) => {
			if (res?.data?.feed_url) {
				setFeedUrl(res.data.feed_url);
				setError(null);
			} else {
				setError('피드 URL을 가져올 수 없습니다.');
			}
		});
	}, [buildPayload]);

	const doReissue = useCallback(() => {
		setReissuing(true);
		api
			.renewCalendarFeed(buildPayload())
			.then((res) => {
				if (res?.data?.feed_url) {
					setFeedUrl(res.data.feed_url);
					setError(null);
				} else {
					setError('피드 재발급에 실패했습니다.');
				}
			})
			.catch(() => setError('피드 재발급에 실패했습니다.'))
			.finally(() => setReissuing(false));
	}, [buildPayload]);

	const onReissue = () => {
		if (
			window.confirm(
				'기존 캘린더 링크는 더 이상 사용할 수 없습니다.\n\n새 링크를 생성하시겠습니까?'
			)
		) {
			doReissue();
		}
	};

	useEffect(() => {
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = '';
		};
	}, []);

	useEffect(() => {
		loadOrCreateFeed().catch(() => setError('캘린더 피드를 불러올 수 없습니다.')).finally(() => setLoading(false));
	}, [loadOrCreateFeed]);

	const onCopy = () => {
		if (feedUrl) {
			navigator.clipboard.writeText(feedUrl).then(() => alert('피드 주소가 클립보드에 복사되었습니다.'));
		}
	};

	return (
		<Overlay onClick={onClose}>
			<Content onClick={(e) => e.stopPropagation()}>
				<Header>
					<Title>캘린더 피드</Title>
					<CloseButton onClick={onClose}>×</CloseButton>
				</Header>
				<Body>
					{loading && !feedUrl && (
						<Message>피드 주소를 불러오는 중...</Message>
					)}
					{error && !feedUrl && (
						<Message error>{error}</Message>
					)}
					{!loading && (feedUrl || error) && (
						<>
							<GuideText>
								아래 링크를 복사하여 iCal 피드를 사용할 수 있는 캘린더 앱(Google Calendar, iCal, Outlook 등)에 붙여넣습니다.
							</GuideText>
							<GuideSubtext>
								Google Calendar은 변경사항이 반영되기까지 최대 24시간 정도 걸릴 수 있습니다. 빠르게 보려면 캘린더를 삭제 후 다시 추가하거나, Apple 캘린더 등을 이용해 보세요.
							</GuideSubtext>
							<UrlInput
								type="text"
								readOnly
								value={feedUrl || ''}
								placeholder={error ? '주소를 불러올 수 없습니다.' : ''}
							/>
							<ButtonRow>
								<CopyButton onClick={onCopy} disabled={!feedUrl}>
									피드 주소 복사
								</CopyButton>
								<ReissueButton onClick={onReissue} disabled={reissuing}>
									{reissuing ? '재발급 중...' : '피드 재발급'}
								</ReissueButton>
							</ButtonRow>
						</>
					)}
				</Body>
			</Content>
		</Overlay>
	);
};

export default CalendarFeedModal;

const Overlay = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: rgba(0, 0, 0, 0.5);
	z-index: 9999;
	display: flex;
	justify-content: center;
	align-items: center;
`;

const Content = styled.div`
	background: white;
	border-radius: 8px;
	min-width: 480px;
	max-width: 90vw;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const Header = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 20px 24px;
	border-bottom: 1px solid #eee;
`;

const Title = styled.div`
	font-family: 'Pretendard';
	font-weight: 600;
	font-size: 18px;
	color: #000;
`;

const CloseButton = styled.button`
	background: none;
	border: 1px solid #2b62ab;
	border-radius: 4px;
	width: 32px;
	height: 32px;
	font-size: 20px;
	line-height: 1;
	cursor: pointer;
	color: #2b62ab;
	padding: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	&:hover {
		background-color: #f0f4fa;
	}
`;

const Body = styled.div`
	padding: 24px;
`;

const Message = styled.div`
	font-family: 'Pretendard';
	font-size: 14px;
	color: ${(p) => (p.error ? '#c00' : '#333')};
`;

const GuideText = styled.p`
	font-family: 'Pretendard';
	font-size: 14px;
	color: #333;
	line-height: 1.5;
	margin: 0 0 16px 0;
`;

const GuideSubtext = styled.p`
	font-family: 'Pretendard';
	font-size: 12px;
	color: #666;
	line-height: 1.4;
	margin: 0 0 12px 0;
`;

const UrlInput = styled.input`
	width: 100%;
	box-sizing: border-box;
	font-family: monospace;
	font-size: 13px;
	padding: 12px 14px;
	border: 1px solid #dddddd;
	border-radius: 5px;
	background: #fafafa;
	color: #333;
	margin-bottom: 20px;
	&:focus {
		outline: none;
		border-color: #2b62ab;
	}
`;

const ButtonRow = styled.div`
	display: flex;
	gap: 12px;
`;

const BaseButton = styled.button`
	padding: 10px 20px;
	border-radius: 5px;
	font-family: 'Pretendard';
	font-weight: 600;
	font-size: 14px;
	cursor: pointer;
	&:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}
`;

const CopyButton = styled(BaseButton)`
	background-color: #2b62ab;
	color: white;
	border: none;
	&:hover:not(:disabled) {
		background-color: #0c438d;
	}
`;

const ReissueButton = styled(BaseButton)`
	background-color: white;
	color: #2b62ab;
	border: 1px solid #2b62ab;
	&:hover:not(:disabled) {
		background-color: #f0f4fa;
	}
`;
